import React, { useState } from 'react';
import { Alert, Box, Button, Paper, Switch, Typography } from '@mui/material';
import { Download } from '@mui/icons-material';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { integrationApiService } from '../services/integrationApi';

export interface WooCommerceCompareSectionProps {
  integrationId: string;
  /** `settings.compareRows`, edited here and saved by the caller with the rest of the settings. */
  compareRows: boolean;
  onChange: (next: boolean) => void;
}

/** Storefront compare for a WooCommerce store: the opt-in, the plugin download and the shortcode. */
const WooCommerceCompareSection: React.FC<WooCommerceCompareSectionProps> = ({ integrationId, compareRows, onChange }) => {
  const { t } = useAppTranslation();
  const { enabled } = useOrgFeatureFlag('product_comparison');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!enabled) return null;

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      await integrationApiService.downloadWooComparePlugin(integrationId);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('integrations.wooCompare.downloadError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: '14px' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, mr: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('integrations.wooCompare.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
            {t('integrations.wooCompare.desc')}
          </Typography>
        </Box>
        <Switch
          size="small"
          checked={compareRows}
          onChange={(e) => onChange(e.target.checked)}
          inputProps={{ 'aria-label': t('integrations.wooCompare.title') }}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" startIcon={<Download />} onClick={download} disabled={busy}>
          {t('integrations.wooCompare.download')}
        </Button>
        <Typography variant="caption" color="text.secondary">
          {t('integrations.wooCompare.shortcode')}
        </Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
};

export default WooCommerceCompareSection;
